import type { CSSProperties, ReactNode } from 'react'
import type { GlassShape } from '@/constants/sans-alcool'

import { useId } from 'react'

interface GlassProps {
    shape: GlassShape
    tint: string
    foam?: boolean
    fizzy?: boolean
    className?: string
}

type ShapeSpec = {
    outline: string
    inner: string
    extra?: string
    shine: string
    top: number
    bottom: number
    full: number
    left: number
    right: number
}

const SHAPES: Record<GlassShape, ShapeSpec> = {
    coupe: {
        outline: 'M16 60 C16 92 34 108 60 108 C86 108 104 92 104 60',
        inner: 'M18 62 C18 91 36 105 60 105 C84 105 102 91 102 62 Z',
        extra: 'M60 108 L60 168 M36 171 Q60 163 84 171',
        shine: 'M26 70 C27 84 33 93 40 98',
        top: 62,
        bottom: 105,
        full: 70,
        left: 34,
        right: 86
    },
    highball: {
        outline: 'M34 36 L37 176 Q37 182 43 182 L77 182 Q83 182 83 176 L86 36',
        inner: 'M36 40 L38.6 170 L81.4 170 L84 40 Z',
        extra: 'M38.6 170 L81.4 170',
        shine: 'M42 52 L43.6 150',
        top: 40,
        bottom: 170,
        full: 56,
        left: 44,
        right: 76
    },
    collins: {
        outline: 'M40 20 L41 178 Q41 184 47 184 L73 184 Q79 184 79 178 L80 20',
        inner: 'M41.5 24 L42.5 172 L77.5 172 L78.5 24 Z',
        extra: 'M42.5 172 L77.5 172',
        shine: 'M46 38 L46.8 156',
        top: 24,
        bottom: 172,
        full: 40,
        left: 48,
        right: 72
    },
    tumbler: {
        outline: 'M26 92 L30 176 Q30 182 36 182 L84 182 Q90 182 90 176 L94 92',
        inner: 'M28 96 L31.6 168 L88.4 168 L92 96 Z',
        extra: 'M31.6 168 L88.4 168',
        shine: 'M35 104 L37 158',
        top: 96,
        bottom: 168,
        full: 108,
        left: 40,
        right: 80
    },
    fizz: {
        outline: 'M40 24 L46 170 Q47 180 60 180 Q73 180 74 170 L80 24',
        inner: 'M42 28 L47.6 166 Q48.5 174 60 174 Q71.5 174 72.4 166 L78 28 Z',
        shine: 'M47 40 L51 150',
        top: 28,
        bottom: 174,
        full: 44,
        left: 52,
        right: 68
    },
    cup: {
        outline: 'M28 94 L35 176 Q36 184 45 184 L75 184 Q84 184 85 176 L92 94',
        inner: 'M30 98 L36.6 172 Q37.5 178 45 178 L75 178 Q82.5 178 83.4 172 L90 98 Z',
        shine: 'M37 108 L41 160',
        top: 98,
        bottom: 178,
        full: 110,
        left: 42,
        right: 78
    }
}

const BUBBLES = [0.18, 0.42, 0.64, 0.86]

const Glass = ({ shape, tint, foam = false, fizzy = false, className }: GlassProps): ReactNode => {
    const id = useId().replace(/[^a-zA-Z0-9_-]/g, '')
    const spec = SHAPES[shape]
    const clip = `${id}-clip`

    return (
        <svg viewBox='0 0 120 200' className={className} aria-hidden='true'>
            <defs>
                <clipPath id={clip}>
                    <path d={spec.inner} />
                </clipPath>
            </defs>

            <path d={spec.inner} fill={tint} opacity='0.07' />

            <rect className='glass-stream' x='59.3' y='0' width='1.4' height={spec.top} fill={tint} opacity='0.9' />

            <g clipPath={`url(#${clip})`}>
                <g
                    className='glass-liquid'
                    style={{ '--empty': `${spec.bottom}px`, '--full': `${spec.full}px` } as CSSProperties}
                >
                    <rect x='0' y='0' width='120' height='200' fill={tint} opacity='0.92' />
                    {foam && <rect x='0' y='0' width='120' height='8' fill='#efe5d6' opacity='0.92' />}
                    <rect x='0' y='0' width='120' height='1.2' fill='#ffffff' opacity='0.35' />
                </g>

                {fizzy && BUBBLES.map((position, index) => (
                    <circle
                        key={position}
                        className='glass-bubble'
                        cx={spec.left + (spec.right - spec.left) * position}
                        cy={spec.bottom - 4}
                        r={0.9 + (index % 2) * 0.5}
                        fill='#ffffff'
                        style={{
                            '--rise': `${-(spec.bottom - spec.full - 10)}px`,
                            '--bd': `${index * 0.7}s`
                        } as CSSProperties}
                    />
                ))}
            </g>

            <path
                d={spec.outline}
                fill='none'
                stroke='rgb(237 230 216 / 0.55)'
                strokeWidth='1'
                strokeLinecap='round'
                strokeLinejoin='round'
                vectorEffect='non-scaling-stroke'
            />

            {spec.extra && (
                <path
                    d={spec.extra}
                    fill='none'
                    stroke='rgb(237 230 216 / 0.35)'
                    strokeWidth='1'
                    strokeLinecap='round'
                    vectorEffect='non-scaling-stroke'
                />
            )}

            <path
                d={spec.shine}
                fill='none'
                stroke='rgb(255 255 255 / 0.22)'
                strokeWidth='1'
                strokeLinecap='round'
                vectorEffect='non-scaling-stroke'
            />
        </svg>
    )
}

export default Glass
